/**
 * 岛屿形状：程序化高度场（纯数据，不建 three 对象）。
 *
 * 为什么不再用几个锥体叠：锥体的等高线永远是圆，从任何角度看都是「一顶帽子」。
 * 真实岛屿的信息量在「不规则的海岸线 + 起伏的山脊 + 分层的地表」上，所以这里
 * 直接生成一张**极坐标高度场**：
 *
 *  1. 海岸线 R(θ) 用 2/3/7/11 次谐波 + 椭圆拉伸，得到海湾、岬角与不对称的长轴；
 *  2. 高度 h(x,z) = 多峰高斯山体 × 域扭曲的山脊噪声 × 岸线遮罩 + 丘陵基底 + 沙滩剖面，
 *     山体本身还带一条脊线，所以有主峰、次峰和走向；
 *  3. 从同一份顶点数据里挑锚点：草坡带 + 坡度平缓的顶点当树（成簇），
 *     海岸线附近的顶点当礁石（半沉），草坡低处当灌木；
 *  4. 每个顶点带一组分区权重（沙 / 岩 / 湿沙 / 明暗 / 枯草），供 Islands.ts 按调色板
 *     烘焙顶点色——这样「从沙滩到草地到岩石」是一条连续的过渡，而不是叠色块。
 *
 * 全部随机来自 @sea/world 的 hash2 / splitmix32（项目里唯一的随机源），
 * 同一个 seed 永远得到同一座岛，运行时不需要再算任何形状。
 */

import { hash2, splitmix32 } from '@sea/world';

/** 一个可以摆树的点（归一化坐标：x/z 乘 radius，y 乘 height 就是世界坐标） */
export interface IslandAnchor {
  readonly x: number;
  readonly z: number;
  /** 该顶点在归一化高度场里的高度（沙滩≈0，山顶≈1） */
  readonly y: number;
  /** 地面法线 y 分量（1 = 平地） */
  readonly ny: number;
  /** 个体缩放系数（构造期定死） */
  readonly scale: number;
  /** 竖直拉伸：同一套树几何体能长成高瘦 / 矮胖（0.8~1.25） */
  readonly stretch: number;
  /** 绕 Y 的朝向（弧度） */
  readonly rot: number;
}

export interface IslandShape {
  /** 归一化坐标的顶点位置（x/z 约在 [-1.2,1.2]，y 在 [-0.2,1]） */
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly index: Uint16Array;
  /** 每顶点 5 个分区权重：sand, rock, wet, shade, dry */
  readonly zones: Float32Array;
  /** 雪顶三角形（共享上面的 positions/normals，另带自己的 color） */
  readonly snowIndex: Uint16Array;
  readonly trees: readonly IslandAnchor[];
  readonly rocks: readonly IslandAnchor[];
  readonly shrubs: readonly IslandAnchor[];
  readonly triangleCount: number;
}

/* ------------------------------------------------------------------ *
 * 网格分辨率：80 段 × 11 圈 = 1680 三角形（+ 圆心扇形），单岛的地形预算。
 * 圈的分布刻意在「海岸/水线」处加密，山体部分疏一些——近处最容易被注意到
 * 的是水与沙的交界，而不是山腰。
 * ------------------------------------------------------------------ */
const SEGMENTS = 80;
const RING_T = [0.3, 0.44, 0.57, 0.68, 0.775, 0.855, 0.915, 0.962, 1.002, 1.06, 1.15] as const;
/** 雪线（归一化高度）：高于它的三角形会单独复制出一层雪壳 */
const SNOW_LINE = 0.74;
/** 树锚点上限 / 礁石 / 灌木 */
const MAX_TREES = 22;
const MAX_ROCKS = 10;
const MAX_SHRUBS = 8;

const TAU = Math.PI * 2;

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** 整数格点哈希 → [0,1)，值噪声的随机源 */
function gridRand(seed: number, ix: number, iz: number): number {
  return hash2((seed ^ Math.imul(iz | 0, 0x27d4eb2d)) >>> 0, ix | 0) / 4294967296;
}

/** 平滑插值的值噪声，[0,1) */
function valueNoise(x: number, z: number, seed: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const xf = x - xi;
  const zf = z - zi;
  const u = xf * xf * (3 - 2 * xf);
  const v = zf * zf * (3 - 2 * zf);
  const a = gridRand(seed, xi, zi);
  const b = gridRand(seed, xi + 1, zi);
  const c = gridRand(seed, xi, zi + 1);
  const d = gridRand(seed, xi + 1, zi + 1);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}

/** 分形噪声，[0,1) */
function fbm(x: number, z: number, seed: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let norm = 0;
  let fx = x;
  let fz = z;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise(fx, fz, (seed + Math.imul(i + 1, 0x9e3779b9)) >>> 0) * amp;
    norm += amp;
    amp *= 0.5;
    fx = fx * 2.03 + 17.1;
    fz = fz * 2.03 - 9.7;
  }
  return sum / norm;
}

/** 脊状噪声：把噪声的「零交叉」变成山脊线，[0,1) */
function ridged(x: number, z: number, seed: number, octaves: number): number {
  let sum = 0;
  let amp = 0.55;
  let norm = 0;
  let fx = x;
  let fz = z;
  for (let i = 0; i < octaves; i++) {
    const n = valueNoise(fx, fz, (seed + Math.imul(i + 3, 0x85ebca6b)) >>> 0);
    const r = 1 - Math.abs(n * 2 - 1);
    sum += r * r * amp;
    norm += amp;
    amp *= 0.5;
    fx = fx * 2.11 - 5.3;
    fz = fz * 2.11 + 8.9;
  }
  return sum / norm;
}

interface Coast {
  readonly ex: number;
  readonly ez: number;
  readonly rot: number;
  readonly amp: readonly [number, number, number, number];
  readonly phase: readonly [number, number, number, number];
  /** 归一化系数：让海岸线的最大半径 = 1，岛不会超出 spec.radius */
  readonly norm: number;
}

interface Summit {
  readonly x: number;
  readonly z: number;
  readonly amp: number;
  readonly sigma: number;
}

interface ShapeGen {
  readonly coast: Coast;
  readonly summits: readonly Summit[];
  readonly spine: readonly [number, number, number, number];
  readonly footPhase: readonly [number, number];
  readonly ridgeSeed: number;
  readonly warpSeedA: number;
  readonly warpSeedB: number;
  readonly hillSeed: number;
  readonly detailSeed: number;
}

/** 海岸线：极坐标半径（未归一化、含椭圆拉伸） */
function coastRadius(coast: Coast, theta: number): number {
  return (
    1 +
    coast.amp[0] * Math.sin(2 * theta + coast.phase[0]) +
    coast.amp[1] * Math.sin(3 * theta + coast.phase[1]) +
    coast.amp[2] * Math.sin(7 * theta + coast.phase[2]) +
    coast.amp[3] * Math.sin(11 * theta + coast.phase[3])
  );
}

/** 海岸线（已归一化）上的点 */
function coastPoint(coast: Coast, theta: number): [number, number] {
  const r = coastRadius(coast, theta);
  const x = coast.ex * Math.cos(theta) * r;
  const z = coast.ez * Math.sin(theta) * r;
  const c = Math.cos(coast.rot);
  const s = Math.sin(coast.rot);
  return [((x * c - z * s) / coast.norm) as number, ((x * s + z * c) / coast.norm) as number];
}

/** 点到线段的距离（山脊用） */
function distToSegment(px: number, pz: number, seg: readonly [number, number, number, number]): number {
  const ax = seg[0];
  const az = seg[1];
  const bx = seg[2];
  const bz = seg[3];
  const vx = bx - ax;
  const vz = bz - az;
  const wx = px - ax;
  const wz = pz - az;
  const len2 = vx * vx + vz * vz;
  const t = len2 > 1e-9 ? clamp01((wx * vx + wz * vz) / len2) : 0;
  const dx = px - (ax + vx * t);
  const dz = pz - (az + vz * t);
  return Math.hypot(dx, dz);
}

/**
 * 沙滩剖面（归一化高度）：
 *  - t < 0.86：内陆，剖面为 0（高度完全由山体决定）
 *  - 0.86 → 0.94：缓缓抬起一道 6% 高的沙脊（干沙）
 *  - 0.94 → 1.00：向水线下降，在 t=1.0 正好回到 0（水线就是画在 t=1 的海岸线）
 *  - 1.00 → 1.18：继续沉到水下 17%，让海浪与沙滩自然相交，而不是被网格边缘切一刀
 */
function beachBase(t: number): number {
  const rise = smoothstep(0.86, 0.94, t) * 0.06;
  const drop = smoothstep(0.94, 1.0, t) * 0.06;
  const sink = smoothstep(1.0, 1.18, t) * 0.17;
  return rise - drop - sink;
}

/** 高度场：给定归一化坐标与「沿这条射线的 t」，返回归一化高度 */
function heightAt(gen: ShapeGen, x: number, z: number, t: number, theta: number): number {
  // 岸线在 tFoot 附近：山体归零，剩下的就是沙滩。
  // 基底和峰体用两条不同长度的遮罩：基底要「缓」，峰脚可以「陡」一点。
  const tf =
    0.895 + 0.05 * Math.sin(2 * theta + gen.footPhase[0]) + 0.025 * Math.sin(5 * theta + gen.footPhase[1]);
  const baseMask = 1 - smoothstep(0.34, tf + 0.05, t);
  const peakMask = 1 - smoothstep(tf - 0.42, tf, t);

  // 低矮的丘陵基底：海岸附近也要抬起来一点，否则整座岛会像一根从水里竖起的锥子。
  // 它同时也是「次峰/丘陵」的主要来源。
  const base = 0.3 * (0.32 + 0.68 * fbm(x * 2.4 + 7.7, z * 2.4 - 4.4, gen.hillSeed, 3));

  // 山体：多峰高斯 + 一条脊线
  let peaks = 0;
  for (const summit of gen.summits) {
    const dx = x - summit.x;
    const dz = z - summit.z;
    peaks += summit.amp * Math.exp(-(dx * dx + dz * dz) / (2 * summit.sigma * summit.sigma));
  }
  peaks += 0.16 * (1 - smoothstep(0.02, 0.22, distToSegment(x, z, gen.spine)));

  // 域扭曲：先把采样坐标揉一下，山脊才不是直的
  const wx = x + 0.28 * (fbm(x * 1.7 + 11.3, z * 1.7 - 5.9, gen.warpSeedA, 2) - 0.5);
  const wz = z + 0.28 * (fbm(x * 1.7 - 7.1, z * 1.7 + 3.7, gen.warpSeedB, 2) - 0.5);
  const ridge = ridged(wx * 3.1, wz * 3.1, gen.ridgeSeed, 2);
  peaks *= 0.6 + 0.7 * ridge;

  // 放射状的冲沟：以峰为中心的脊/谷交替，是火山岛最典型的纹理
  const gully = 0.08 * Math.sin(theta * 7 + gen.footPhase[1] * 0.7) + 0.045 * Math.sin(theta * 11 - gen.footPhase[0]);
  peaks *= 1 + gully * peakMask;

  // 次级丘陵与细碎起伏
  const hills = fbm(x * 3.6 + 4.3, z * 3.6 - 2.9, gen.hillSeed ^ 0x51ed270b, 3);
  let mountain = base * baseMask + peaks * peakMask;
  mountain += 0.26 * Math.max(0, hills - 0.42) * baseMask;
  mountain += 0.06 * (fbm(x * 7.5, z * 7.5, gen.detailSeed, 3) - 0.5) * baseMask;
  mountain += 0.024 * (fbm(x * 16.0, z * 16.0, gen.detailSeed ^ 0x27d4eb2f, 2) - 0.5) * baseMask;

  if (mountain < 0) mountain = 0;

  // 沙滩上的小起伏（沙丘/冲刷痕）：贴着水线的几米内有明暗变化，才不像一块平板
  const beachBand = smoothstep(0.78, 0.9, t) * (1 - smoothstep(1.0, 1.12, t));
  mountain += beachBase(t);
  mountain += beachBand * 0.02 * (valueNoise(x * 13 + 3.1, z * 13 - 2.2, gen.detailSeed) - 0.5);
  return mountain;
}

/**
 * 顶点法线：按索引三角形面积加权累加，再归一化。
 * 半径顶点在网格里是共享的，所以得到的是平滑法线；想要低多边形的硬边，
 * 由材质上的 flatShading 决定，不需要拆顶点。
 */
function computeNormals(positions: Float32Array, index: Uint16Array): Float32Array {
  const normals = new Float32Array(positions.length);
  for (let i = 0; i < index.length; i += 3) {
    const ia = index[i] as number;
    const ib = index[i + 1] as number;
    const ic = index[i + 2] as number;
    const ax = positions[ia * 3] as number;
    const ay = positions[ia * 3 + 1] as number;
    const az = positions[ia * 3 + 2] as number;
    const e1x = (positions[ib * 3] as number) - ax;
    const e1y = (positions[ib * 3 + 1] as number) - ay;
    const e1z = (positions[ib * 3 + 2] as number) - az;
    const e2x = (positions[ic * 3] as number) - ax;
    const e2y = (positions[ic * 3 + 1] as number) - ay;
    const e2z = (positions[ic * 3 + 2] as number) - az;
    // e1 × e2（不用归一化：叉积长度自带面积权重）
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    normals[ia * 3] += nx;
    normals[ia * 3 + 1] += ny;
    normals[ia * 3 + 2] += nz;
    normals[ib * 3] += nx;
    normals[ib * 3 + 1] += ny;
    normals[ib * 3 + 2] += nz;
    normals[ic * 3] += nx;
    normals[ic * 3 + 1] += ny;
    normals[ic * 3 + 2] += nz;
  }
  for (let i = 0; i < normals.length; i += 3) {
    const x = normals[i] as number;
    const y = normals[i + 1] as number;
    const z = normals[i + 2] as number;
    const len = Math.hypot(x, y, z);
    if (len > 1e-8) {
      normals[i] = x / len;
      normals[i + 1] = y / len;
      normals[i + 2] = z / len;
    } else {
      normals[i + 1] = 1;
    }
  }
  return normals;
}

interface VertexInfo {
  readonly ring: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly ny: number;
}

/**
 * 在符合条件的顶点里挑锚点。
 * 成簇的关键是 `clusterRadius`：先在允许区里随机取 2~3 个「树丛中心」，
 * 再只接受中心附近的点，所以树会一丛一丛地长，而不是绕岛均匀撒一圈。
 * 每个簇先分好候选桶，再按随机起点顺序扫描——保证一定能挑到足够多、又不会挤在一起的点。
 */
function pickAnchors(
  rng: () => number,
  candidates: readonly VertexInfo[],
  count: number,
  clusterRadius: number,
  minGap: number,
): IslandAnchor[] {
  if (candidates.length === 0) return [];

  const clusters: VertexInfo[] = [];
  const clusterCount = Math.min(candidates.length, 2 + Math.floor(rng() * 2));
  for (let c = 0; c < clusterCount; c++) {
    // 尽量让几个簇心互相拉开，避免全挤在同一个小坡上
    let best: VertexInfo | null = null;
    let bestScore = -1;
    for (let attempt = 0; attempt < 12; attempt++) {
      const cand = candidates[Math.floor(rng() * candidates.length)] as VertexInfo;
      let score = rng() * 0.2;
      for (const other of clusters) score += Math.hypot(cand.x - other.x, cand.z - other.z);
      if (score > bestScore) {
        bestScore = score;
        best = cand;
      }
    }
    if (best) clusters.push(best);
  }

  // 每个簇心附近可用的顶点
  const buckets: VertexInfo[][] = [];
  for (const cluster of clusters) {
    const bucket: VertexInfo[] = [];
    for (const cand of candidates) {
      if (Math.hypot(cand.x - cluster.x, cand.z - cluster.z) <= clusterRadius) bucket.push(cand);
    }
    // 打乱一遍：候选点原本是按圈/角度排列的，不打乱的话树会沿着网格排成整齐的一行
    for (let i = bucket.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = bucket[i] as VertexInfo;
      bucket[i] = bucket[j] as VertexInfo;
      bucket[j] = tmp;
    }
    if (bucket.length > 0) buckets.push(bucket);
  }
  if (buckets.length === 0) return [];

  const out: IslandAnchor[] = [];
  const minGap2 = minGap * minGap;
  for (let i = 0; i < count; i++) {
    const bucket = buckets[i % buckets.length] as VertexInfo[];
    const start = Math.floor(rng() * bucket.length);
    let picked: VertexInfo | null = null;
    for (let step = 0; step < bucket.length; step++) {
      const cand = bucket[(start + step) % bucket.length] as VertexInfo;
      let ok = true;
      for (const other of out) {
        if ((other.x - cand.x) ** 2 + (other.z - cand.z) ** 2 < minGap2) {
          ok = false;
          break;
        }
      }
      if (ok) {
        picked = cand;
        break;
      }
    }
    if (!picked) continue;
    out.push({
      x: picked.x,
      z: picked.z,
      y: picked.y,
      ny: picked.ny,
      scale: 0.78 + rng() * 0.44,
      stretch: 0.82 + rng() * 0.4,
      rot: rng() * TAU,
    });
  }
  return out;
}

/** 生成一座岛的全部形状数据。同一个 seed 结果永远一致。 */
export function buildIslandShape(seed: number): IslandShape {
  const rng = splitmix32(seed);

  /* ---------------- 1. 海岸线 ---------------- */
  const coast: Coast = {
    ex: 1 + rng() * 0.32,
    ez: 0.84 + rng() * 0.26,
    rot: rng() * TAU,
    // 谐波幅度：2/3 次给「海湾与岬角」的大形，7/11 次把岸线揉毛
    amp: [0.07 + rng() * 0.05, 0.12 + rng() * 0.09, 0.06 + rng() * 0.05, 0.03 + rng() * 0.03] as const,
    phase: [rng() * TAU, rng() * TAU, rng() * TAU, rng() * TAU] as const,
    norm: 1,
  };
  // 扫一圈求最大半径，归一化后岛不会超过 spec.radius
  let maxR = 0.0001;
  for (let i = 0; i < 512; i++) {
    const theta = (i / 512) * TAU;
    const r = coastRadius(coast, theta);
    maxR = Math.max(maxR, Math.abs(r) * Math.max(coast.ex, coast.ez));
  }
  const coastN: Coast = { ...coast, norm: maxR };

  /* ---------------- 2. 山体 ---------------- */
  const summits: Summit[] = [];
  const mainAngle = rng() * TAU;
  const mainDist = rng() * 0.26;
  summits.push({
    x: Math.cos(mainAngle) * mainDist,
    z: Math.sin(mainAngle) * mainDist,
    amp: 0.9,
    // 主峰不能太窄：sigma 太小就是从水里直接竖起一根针（又变回「帽子」）
    sigma: 0.32 + rng() * 0.14,
  });
  const extra = 2 + (rng() < 0.5 ? 1 : 0);
  for (let i = 0; i < extra; i++) {
    const angle = rng() * TAU;
    const dist = 0.24 + rng() * 0.45;
    summits.push({
      x: Math.cos(angle) * dist,
      z: Math.sin(angle) * dist,
      amp: 0.26 + rng() * 0.34,
      sigma: 0.16 + rng() * 0.16,
    });
  }

  // 山脊线：一端在岛内偏一侧，另一端往反方向拉出去
  const spineAngle = rng() * TAU;
  const spineCenter = 0.1 + rng() * 0.22;
  const spineHalf = 0.32 + rng() * 0.2;
  const spine: readonly [number, number, number, number] = [
    Math.cos(spineAngle + 0.5) * spineCenter - Math.cos(spineAngle) * spineHalf,
    Math.sin(spineAngle + 0.5) * spineCenter - Math.sin(spineAngle) * spineHalf,
    Math.cos(spineAngle + 0.5) * spineCenter + Math.cos(spineAngle) * spineHalf,
    Math.sin(spineAngle + 0.5) * spineCenter + Math.sin(spineAngle) * spineHalf,
  ];

  const gen: ShapeGen = {
    coast: coastN,
    summits,
    spine,
    footPhase: [rng() * TAU, rng() * TAU] as const,
    ridgeSeed: (seed ^ 0x3c6ef372) >>> 0,
    warpSeedA: (seed ^ 0x510e527f) >>> 0,
    warpSeedB: (seed ^ 0x1f83d9ab) >>> 0,
    hillSeed: (seed ^ 0x5be0cd19) >>> 0,
    detailSeed: (seed ^ 0xa54ff53a) >>> 0,
  };

  /* ---------------- 3. 顶点网格 ---------------- */
  const vertexCount = 1 + RING_T.length * SEGMENTS;
  const positions = new Float32Array(vertexCount * 3);
  const index = new Uint16Array((SEGMENTS + (RING_T.length - 1) * SEGMENTS * 2) * 3);
  const ringOf = new Uint8Array(vertexCount);
  /** 每个顶点的极坐标参数 t（0 = 岛心，1 = 海岸线）：顶点色分区需要它来画「沿岸沙带」 */
  const paramT = new Float32Array(vertexCount);

  // 圆心
  positions[1] = heightAt(gen, 0, 0, 0, 0);
  let maxHeight = Math.max(0.0001, positions[1] as number);

  let vertex = 1;
  for (let k = 0; k < RING_T.length; k++) {
    const t = RING_T[k] as number;
    // 每一圈整体转一点点角度：三角形不会被排成放射状的「车轮辐条」
    const ringPhase = (rng() - 0.5) * (TAU / SEGMENTS) * 0.9;
    for (let j = 0; j < SEGMENTS; j++) {
      const theta = (j / SEGMENTS) * TAU + ringPhase;
      // 顶点半径抖动 ±1.5%：海岸线因此有一点点毛边
      const jitter = 1 + (gridRand(seed ^ 0x2545f491, k, j) - 0.5) * 0.03;
      const tt = t * jitter;
      const [cx, cz] = coastPoint(coastN, theta);
      const x = cx * tt;
      const z = cz * tt;
      const y = heightAt(gen, x, z, tt, theta);
      positions[vertex * 3] = x;
      positions[vertex * 3 + 1] = y;
      positions[vertex * 3 + 2] = z;
      paramT[vertex] = tt;
      if (y > maxHeight) maxHeight = y;
      ringOf[vertex] = k;
      vertex++;
    }
  }

  // 以最高点为 1 归一化：这样 spec.height 就是山顶高度
  for (let i = 1; i < positions.length; i += 3) {
    positions[i] = (positions[i] as number) / maxHeight;
  }

  /* ---------------- 4. 索引 ---------------- */
  let tri = 0;
  const pushTri = (a: number, b: number, c: number): void => {
    index[tri++] = a;
    index[tri++] = b;
    index[tri++] = c;
  };
  // 圆心扇形（注意绕序：从上方看是逆时针，法线朝 +Y）
  for (let j = 0; j < SEGMENTS; j++) {
    const a = 1 + j;
    const b = 1 + ((j + 1) % SEGMENTS);
    pushTri(0, b, a);
  }
  // 每一圈之间的四边形
  for (let k = 0; k < RING_T.length - 1; k++) {
    const inner = 1 + k * SEGMENTS;
    const outer = 1 + (k + 1) * SEGMENTS;
    for (let j = 0; j < SEGMENTS; j++) {
      const j1 = (j + 1) % SEGMENTS;
      const ia = inner + j;
      const ia1 = inner + j1;
      const ib = outer + j;
      const ib1 = outer + j1;
      pushTri(ia, ib1, ib);
      pushTri(ia, ia1, ib1);
    }
  }

  const normals = computeNormals(positions, index);

  /* ---------------- 5. 锚点：树 / 礁石 / 灌木 ---------------- */
  const all: VertexInfo[] = [];
  for (let v = 1; v < vertexCount; v++) {
    all.push({
      ring: ringOf[v] as number,
      x: positions[v * 3] as number,
      y: positions[v * 3 + 1] as number,
      z: positions[v * 3 + 2] as number,
      ny: normals[v * 3 + 1] as number,
    });
  }

  // 树：草坡带里、坡度够缓、离水面够高的顶点。成簇（2~3 个树丛中心）。
  const treeCandidates = all.filter(
    (v) => v.ring <= 6 && v.y > 0.075 && v.y < 0.66 && v.ny > 0.5,
  );
  const trees = pickAnchors(rng, treeCandidates, MAX_TREES, 0.26, 0.038);

  // 礁石：海岸线附近（沙滩与水下），半沉半露
  const rockCandidates = all.filter((v) => v.ring >= 4 && v.y < 0.14);
  const rocks = pickAnchors(rng, rockCandidates, MAX_ROCKS, 0.55, 0.05);

  // 灌木：草坡低处
  const shrubCandidates = all.filter(
    (v) => v.ring <= 7 && v.y > 0.04 && v.y < 0.42 && v.ny > 0.7,
  );
  const shrubs = pickAnchors(rng, shrubCandidates, MAX_SHRUBS, 0.4, 0.04);

  /* ---------------- 6. 分区权重（烘焙顶点色用） ---------------- */
  // 树丛底下的草押暗一圈：远看时「哪里是树林」一眼就能读出来
  const canopyShade = new Float32Array(vertexCount);
  const canopyR = 0.12;
  for (const tree of trees) {
    for (let v = 0; v < vertexCount; v++) {
      const dx = (positions[v * 3] as number) - tree.x;
      const dz = (positions[v * 3 + 2] as number) - tree.z;
      const d = Math.hypot(dx, dz);
      if (d >= canopyR) continue;
      const k = 1 - d / canopyR;
      if (k > canopyShade[v]!) canopyShade[v] = k;
    }
  }

  const zones = new Float32Array(vertexCount * 5);
  const colorSeedA = (seed ^ 0x7f4a7c15) >>> 0;
  const colorSeedB = (seed ^ 0x9e3779b9) >>> 0;
  const colorSeedC = (seed ^ 0x85ebca6b) >>> 0;
  for (let v = 0; v < vertexCount; v++) {
    const x = positions[v * 3] as number;
    const y = positions[v * 3 + 1] as number;
    const z = positions[v * 3 + 2] as number;
    const t = paramT[v] as number;
    const ny = normals[v * 3 + 1] as number;
    const slope = 1 - ny;

    const patch = valueNoise(x * 6.2 + 1.7, z * 6.2 - 3.4, colorSeedA);
    const grain = valueNoise(x * 14.5, z * 14.5, colorSeedB);
    const broad = valueNoise(x * 3.1, z * 3.1, colorSeedC);

    // 沙：两条依据取并集——「高度很低」和「沿岸一圈」。
    // 沿岸带只按径向位置判定（而不是高度），否则缓坡低地会被一路刷成沙地。
    const sandByHeight = smoothstep(0.05, 0.017, y + (patch - 0.5) * 0.018);
    const sandByShore = smoothstep(0.9, 0.988, t) * 0.95;
    const sand = Math.max(sandByHeight, sandByShore);
    // 湿沙：水线上下（比干沙深一档，水线才读得出来）
    const wet = smoothstep(0.03, 0.006, y);
    // 岩：真陡的坡（约 > 45°）、峰顶，以及草地上零星的裸露岩斑
    const rockSlope = smoothstep(0.52, 0.78, slope);
    const rockHigh = smoothstep(0.7, 0.93, y) * (0.32 + 0.42 * grain);
    const rockPatch = smoothstep(0.72, 0.95, patch) * smoothstep(0.2, 0.42, slope) * 0.8;
    let rock = Math.max(rockSlope, rockHigh, rockPatch);
    // 沙滩上不该长出岩石斑
    rock *= 1 - sand * 0.85;
    if (rock > 1) rock = 1;

    // 枯草斑：低处平缓的草坡上一块块发黄的草地，打破「一整片绿」
    const dry = smoothstep(0.54, 0.8, patch) * (1 - sand) * (1 - rock) * smoothstep(0.02, 0.1, y) * 0.8;

    zones[v * 5] = sand;
    zones[v * 5 + 1] = rock;
    zones[v * 5 + 2] = wet;
    zones[v * 5 + 3] = clamp01(0.16 + broad * 0.5 + grain * 0.34 - (canopyShade[v] as number) * 0.42);
    zones[v * 5 + 4] = dry;
  }

  /* ---------------- 7. 雪顶三角形 ---------------- */
  const snow: number[] = [];
  for (let i = 0; i < tri; i += 3) {
    const a = index[i] as number;
    const b = index[i + 1] as number;
    const c = index[i + 2] as number;
    if (
      (positions[a * 3 + 1] as number) > SNOW_LINE &&
      (positions[b * 3 + 1] as number) > SNOW_LINE &&
      (positions[c * 3 + 1] as number) > SNOW_LINE &&
      (normals[a * 3 + 1] as number) > 0.35 &&
      (normals[b * 3 + 1] as number) > 0.35 &&
      (normals[c * 3 + 1] as number) > 0.35
    ) {
      snow.push(a, b, c);
    }
  }

  return {
    positions,
    normals,
    index,
    zones,
    snowIndex: Uint16Array.from(snow),
    trees,
    rocks,
    shrubs,
    triangleCount: tri / 3,
  };
}
